<template>
    <div class="map-container">
        <div class="loading-overlay" v-show="showLoadingOverlay">
            <img src="@/assets/loading.gif" />
        </div>
        <div class="static-loading-overlay" v-show="staticMapLayersLoading">
            <!-- spinner will work with determinate and indeterminate, currently set to indeterminate as it looks better, thumbnails load very quickly -->
            <md-progress-spinner md-mode="indeterminate" class="static-loading-spinner md-accent" :md-value="percentStaticLayersLoaded">
            </md-progress-spinner>
        </div>
        <div class="google-map" ref="googleMap" id="googleMap"></div>

        <!-- waits until google maps finishes loading to set the custom components up - this allows bbox to be properly set -->
        <template v-if="mapLoaded">
            <GoogleMapBasemapView
                :google="google"
                :map="map"
                :orgCustomisations="orgCustomisations"
            />
            <GoogleMapClearMap
                v-if="!isDashboardMap && showClearMap"
                :google="google"
                :map="map"
                ref="clearMap"
                data-html2canvas-ignore="true"
            />
            <GoogleMapSave
                :google="google"
                :map="map"
                data-html2canvas-ignore="true"
            />
            <GoogleMapDrawing
                v-if="!isDashboardMap && showMapDrawing"
                :google="google"
                :map="map"
                :selected-area="selectedArea"
                :drawingIsLocked="drawingIsLocked"
                :showLockControls="showLockControls"
                @add-area="addArea"
                ref="mapDrawing"
                @update-latlng="updateLatLng"
                @disable-run="disableRun"
                data-html2canvas-ignore="true"
                v-on:drawing-mode-entered="handleDrawingMode"
                v-on:hide-lock-controls="hideLockControls"
            />
            <GoogleMapLayersView
                :google="google"
                :map="map"
                :mapLayers="mapLayers"
                :mapBBox="getTotalBBox"
                ref="themap"
                @update-map-layers="updateMapLayers"
                @layer-removed="(blockID) => removeLegend(blockID)"
                data-html2canvas-ignore="true"
            />
            <GoogleMapMarker
                v-for="marker in markers"
                :key="marker.id"
                :marker="marker"
                :google="google"
                :map="map"
            />
            <GoogleMapLayerContainer
                v-for="mapLayer in mapLayers"
                :key="`mapLayer-${mapLayer.mapLayerID}-${layerCounter}`"
                :google="google"
                :map="map"
                :mapLayer="mapLayer"
                :showHighResImagery="showHighResImagery"
                @loading-map-url="loadingNewMapUrl"
                @map-url-loaded="mapUrlLoaded"
                @tiles-loading="setTilesLoading"
            />
            <!-- <GoogleHighResToggle
            :google="google"
            :map="map"
            @toggle-high-res="(value) => showHighResImagery = value"
            v-show = showHighResToggle
            data-html2canvas-ignore="true"
            /> -->
            <GoogleMapLockMode
                v-if="showLockControls"
                :google="google"
                :map="map"
                :drawType="drawType"
                ref="lockMode"
                data-html2canvas-ignore="true"
                v-on:hide-lock-controls="hideLockControls"
                v-on:drawing-lock-status="handleDrawingLock"
            />

            <GoogleMapAreasView
                v-if="!isDashboardMap && showMapAreas"
                :google="google"
                :map="map"
                v-model="selectedArea"
                ref="areaView"
                data-html2canvas-ignore="true"
            />
            <GoogleMapLegend
                :google="google"
                :map="map"
                :legends="legends"
            />
            <GoogleMapTimeline
                v-if="!isDashboardMap"
                :google="google"
                :map="map"
                :mapLayers="mapLayers"
                data-html2canvas-ignore="true"
            />
            <GoogleMapLatLng
                :google="google"
                :map="map"
                ref="latLngDisplay"
                data-html2canvas-ignore="true"
            />
            <GoogleMapLoadingIndicator
                :google="google"
                :map="map"
                :tilesLoading="tilesLoading"
                data-html2canvas-ignore="true"
            />
        </template>
    </div>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
import GoogleMapMarker from "@/components/ResultMap/GoogleMapMarker";
import GoogleMapLayersView from "@/components/ResultMap/GoogleMapLayersView";
import GoogleMapDrawing from "@/components/ResultMap/GoogleMapDrawing";
import GoogleMapClearMap from "@/components/ResultMap/GoogleMapClearMap";
import GoogleMapAreasView from "@/components/ResultMap/GoogleMapAreasView";
import GoogleMapLegend from "@/components/ResultMap/GoogleMapLegend";
import GoogleMapTimeline from "@/components/ResultMap/GoogleMapTimeline";
import GoogleMapLayerContainer from "@/components/ResultMap/GoogleMapLayerContainer";
import GoogleMapLatLng from "@/components/ResultMap/GoogleMapLatLng";
import GoogleMapSave from "@/components/ResultMap/GoogleMapSave";
import GoogleMapLoadingIndicator from "@/components/ResultMap/GoogleMapLoadingIndicator";
import GoogleMapBasemapView from "@/components/ResultMap/GoogleMapBasemapView";
import GoogleMapLockMode from "@/components/ResultMap/GoogleMapLockMode";
import { mapSettings } from "@/constants/mapSettings";
import { mapsApiKey, firebaseConfig } from "@/firebase.js";
import { AreaService } from "@/services/area.service";
import { AuthService } from "@/services/auth.service";
import { ConfigService } from '@/services/config.service';
import * as configConstants from "@/config/constants";
import { OrgConfig } from "@/config/main";
import { Loader } from "@googlemaps/js-api-loader";

export default {
    name: "TheResultMap",
    components: {
        GoogleMapMarker,
        GoogleMapLayersView,
        GoogleMapDrawing,
        GoogleMapAreasView,
        GoogleMapClearMap,
        GoogleMapLegend,
        GoogleMapTimeline,
        GoogleMapLayerContainer,
        GoogleMapLatLng,
        GoogleMapSave,
        GoogleMapLoadingIndicator,
        GoogleMapBasemapView,
        GoogleMapLockMode
    },
    props: {
        orgCustomisations: {
            type: Object,
            required: true,
        },
        blocks: {
            type: Array,
            required: true,
        },
        showLoadingOverlay: {
            type: Boolean,
        },
        isDashboardMap: {
            type: Boolean,
            required: true,
        },
    },
    data: () => ({
        markers: [
            /*{
        id: "edinburgh",
        position: { lat: 55.9533, lng: -3.1883 }
      }*/
        ],
        selectedArea: {},
        mapCenter: {
            lat: 0,
            lng: 0,
        },
        mapBlocks: {},
        mapLayers: [],
        legends: [],
        layerCounter: 0,
        noStaticMapLayersLoading: 0,
        totalNoStaticMapLayers: 0,
        tilesLoading: false,
        showHighResImagery: true,
        mapLoaded: false,
        user: null,
        orgConfig: null,
        drawType: '',
        showLockControls: false,
        drawingIsLocked: false,
        google: null,
        map: null,
    }),
    computed: {
        /**
         * Return the mapsApiKey constant from firebase.js, or if it's not set, the default project apiKey.
         */
        mapsApiKey() {
            return mapsApiKey || firebaseConfig.apiKey;
        },
        mapConfig() {
            return {
                ...mapSettings,
                center: this.mapCenter,
                fullscreenControlOptions: {
                    position: this.google.maps.ControlPosition.BOTTOM_RIGHT,
                },
                zoomControlOptions: {
                    position: this.google.maps.ControlPosition.RIGHT_BOTTOM,
                },
            };
        },
        getTotalBBox() {
            let totalBBox = null;
            if (this.mapLayers.length > 0) {
                let mapLayers = this.mapLayers;
                let coords = [];
                for (var i = 0; i < mapLayers.length; i++) {
                    let layer = mapLayers[i];
                    if (layer.bbox !== undefined) {
                        coords.push(layer.bbox["NE"]);
                        coords.push(layer.bbox["SW"]);
                    }
                }

                var lats = [];
                var lngs = [];

                for (var j = 0; j < coords.length; j++) {
                    lats.push(coords[j][1]);
                    lngs.push(coords[j][0]);
                }

                // calc the min and max lng and lat
                var minlat = Math.min.apply(null, lats),
                    maxlat = Math.max.apply(null, lats);
                var minlng = Math.min.apply(null, lngs),
                    maxlng = Math.max.apply(null, lngs);

                // create a bounding rectangle that can be used in leaflet
                totalBBox = [
                    [minlng, minlat],
                    [maxlng, maxlat],
                ];
            }
            return totalBBox;
        },

        blocksWithLayers() {
            return this.blocks.filter((block) => {
                if (block.runResult.layers) {
                    return (
                        block.runResult.layers.length &&
                        block.isLoading === false &&
                        block.runResult.status === "completed"
                    );
                }
            });
        },
        blocksWithResultAndLegend() {
            let blocksWithLegend = this.blocks.filter((block) => {
                if (
                    block.runResult.layers &&
                    block.runResult.layers.length > 0
                ) {
                    return (
                        block.runResult.layers[0].mapURL &&
                        block.runResult.legend
                    );
                }
                return false;
            });
            return blocksWithLegend;
        },
        staticMapLayersLoading() {
            // if there are layers still loading
            if (this.noStaticMapLayersLoading > 0) {
                return true;
            } else {
                return false;
            }
        },
        percentStaticLayersLoaded() {
            return (
                ((this.totalNoStaticMapLayers - this.noStaticMapLayersLoading) /
                    this.totalNoStaticMapLayers) *
                100
            );
        },
        showHighResToggle() {
            let timeseriesGroups = this.mapLayers.filter((mapLayer) => {
                return mapLayer.isGroup;
            });
            return timeseriesGroups.length > 0;
        },
        // Map features
        showMapDrawing() {
            return OrgConfig.isFeatureShown(this.orgConfig, configConstants.FEATURES.MAP_DRAWING_TOOLS);
        },
        showClearMap() {
            return OrgConfig.isFeatureShown(this.orgConfig, configConstants.FEATURES.CLEAR_MAP);
        },
        showMapAreas() {
            return OrgConfig.isFeatureShown(this.orgConfig, configConstants.FEATURES.MAP_AREA_TOOLS);
        }
    },
    async mounted() {
        this.clearAreasSubscription = AreaService.clearAreas$.subscribe(
            function () {
                this.clearMap();
            }.bind(this)
        );
        this.subscription = AuthService.loggedUser$.subscribe(
            (user) => (this.user = user)
        );
        ConfigService.getOrgConfig(this.user.orgId).then(function(config){
            this.orgConfig = config;
        }.bind(this));

        const loader = new Loader({
            apiKey: this.mapsApiKey,
            libraries: ["geometry", "drawing"], // MUST BE DOUBLE QUOTES ""
            version: "quarterly"
        })
        loader.load().then((google) => {
            this.google = google;
            this.initializeMap()
        }).catch(e => {
            console.error("Failed to load map", e)
        })
    },
    beforeDestroy() {
        this.clearAreasSubscription.unsubscribe();
    },
    methods: {
        initializeMap() {
            const mapContainer = this.$refs.googleMap;
            this.map = new this.google.maps.Map(mapContainer, this.mapConfig);

            // if this is the open access dashboard
            // set the default map type to 'hybrid'
            if(this.ebxUseOpenAccessDashboard){
                this.map.mapTypeId = 'terrain';
            }

            this.google.maps.event.addListenerOnce(this.map, 'tilesloaded', ()=> {
                console.log("map finished loading")
                this.mapLoaded = true;
            })
            if(this.ebxUseOpenAccessDashboard){
                this.google.maps.Map.mapTypeId = 'terrain';
            }
            console.log("google event",this.google.maps.event)
        },
        handleDrawingLock(isLocked) {
            this.drawingIsLocked = isLocked;
        },
        handleDrawingMode(mode) {
            this.drawType = mode;
            this.showLockControls = true;
        },
        hideLockControls() {
            this.showLockControls = false;
        },
        clearMap() {
            this.displayBlocks = [];
            this.mapLayers = [];
            this.legends = [];
            this.totalNoStaticMapLayers = 0;
        },
        updateMapLayers(newMapLayers, overlayMapTypes) {
            this.layerCounter++;
            overlayMapTypes.clear();
            this.mapLayers = [];
            // reversed so in menu of layers top layer is top in menu
            let reversedMapLayers = newMapLayers.slice().reverse();
            reversedMapLayers.forEach((layer, index) => {
                layer.mapLayerID = index;
                this.mapLayers.push(layer);
            });
        },
        addArea(areaName) {
            this.$refs.areaView.addArea(areaName);
        },
        updateLatLng(latLngEvent) {
            this.$refs.latLngDisplay.updateCoordinates(latLngEvent);
        },
        loadingNewMapUrl() {
            // loading static thumbnail url
            this.noStaticMapLayersLoading++;
            this.totalNoStaticMapLayers++;
        },
        mapUrlLoaded() {
            // static thumbnail url loaded
            this.noStaticMapLayersLoading--;
        },
        setTilesLoading(value) {
            // update whether tiles are loading or not
            this.tilesLoading = value;
        },
        disableRun(value) {
            this.$emit("disable-run", value);
        },
        removeLegend(blockID) {
            let legendIndex = this.legends.findIndex(
                (legend) => legend.blockID === blockID
            );
            if (legendIndex !== -1) {
                this.legends.splice(legendIndex, 1);
            }
        },
    },
    watch: {
        blocksWithLayers(blocksWithLayers) {
            blocksWithLayers.forEach((block) => {
                block = JSON.parse(JSON.stringify(block));

                let mapLayers = [];

                if (block.runResult.isGroup === true) {
                    let mapLayerGroup = {
                        mapLayerID: this.mapLayers.length, // you cannot have more than one image in a overlayMapType thus this ID is not accurate
                        blockID: block.id,
                        blockName: block.runResult.name,
                        country: block.runResult.country,
                        layers: block.runResult.layers,
                        bbox: block.runResult.bbox,
                        selectedLayer: 0,
                        isGroup: true,
                        maxOpacity: 1,
                    };

                    mapLayers.push(mapLayerGroup);
                } else {
                    block.runResult.layers.forEach((layer) => {
                        let mapLayer = {
                            mapLayerID:
                                this.mapLayers.length + mapLayers.length, //this ID is not consistent with the actual layer stack
                            blockID: block.id,
                            blockName: block.runResult.name,
                            country: block.runResult.country,
                            bbox: block.runResult.bbox,
                            layers: [layer],
                            selectedLayer: 0,
                            isGroup: false,
                        };
                        mapLayers.push(mapLayer);
                    });
                }
                let mapLayerExists = (mapLayer) => {
                    let index;
                    if (mapLayer.isGroup) {
                        index = this.mapLayers.findIndex(
                            (stateMapLayer) =>
                                stateMapLayer.blockID === mapLayer.blockID
                        );
                    } else {
                        index = this.mapLayers.findIndex(
                            (stateMapLayer) =>
                                stateMapLayer.layers[0].mapid ===
                                mapLayer.layers[0].mapid
                        );
                    }

                    if (index !== -1) {
                        return true;
                    }
                    return false;
                };

                mapLayers.forEach((mapLayer) => {
                    if (!mapLayerExists(mapLayer)) {
                        this.mapLayers.push(mapLayer);
                    }
                });
            });
        },
        blocksWithResultAndLegend(blocks) {
            blocks.forEach((block) => {
                block = JSON.parse(JSON.stringify(block));
                let legend = block.runResult.legend;
                legend.blockID = block.id;
                legend.layerName = block.runResult.layerName;

                const legendExists = (legend) => {
                    for (var i = 0; i < this.legends.length; i++) {
                        if (this.legends[i].blockID === legend.blockID) {
                            return true;
                        }
                    }
                    return false;
                };

                if (!legendExists(legend)) {
                    this.legends.push(legend);
                }
            });
        },
    },
    created() {
        this.$root.$on('oa-dashboard-reload', () => {
            this.clearMap();
        })
    }
};
</script>
<style scoped>
.map-container {
    display: grid;
    height: 100%;
    width: 100%;
}

.google-map {
    width: 100%;
    height: 100%;
    grid-area: 1 / 1;
}

.loading-overlay {
    display: flex;
    grid-area: 1 / 1;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    z-index: 99;
}

.loading-overlay > img {
    display: block;
    max-height: 150px;
    max-width: 150px;
    margin: auto;
}

.static-loading-overlay {
    display: flex;
    grid-area: 1 / 1;
    width: 100%;
    height: 100%;
    z-index: 99;
    background: rgba(0, 0, 0, 0.7);
}

.static-loading-spinner {
    max-height: 150px;
    max-width: 150px;
    margin: auto;
}

</style>